import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock } from 'lucide-react';
import { useFeatureAccess } from '../contexts/FeatureAccessContext';
import { useTabActivity } from '../contexts/TabActivityContext';
import { FeatureAccessKey, getUpgradeLabel } from '../navigation/featureAccess';
import './FeatureAction.css';

/** Keep locked actions discoverable by pointer, keyboard and touch. */
const FeatureAction: React.FC<{
  feature?: FeatureAccessKey | null;
  children: React.ReactElement;
}> = ({ feature = 'workspace_mutation', children }) => {
  const { account, getLockedFeature } = useFeatureAccess();
  const { isActive, portalContainer } = useTabActivity();
  const locked = feature ? getLockedFeature(feature) : null;
  const id = useId();
  const wrapper = useRef<HTMLSpanElement>(null);
  const disclosure = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const open = () => {
    const rect = wrapper.current?.querySelector('button')?.getBoundingClientRect();
    if (rect) setPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 268)),
      top: rect.bottom + 80 < window.innerHeight ? rect.bottom : Math.max(8, rect.top - 76),
    });
  };
  const contains = (target: EventTarget | null) => target instanceof Node
    && Boolean(wrapper.current?.contains(target) || disclosure.current?.contains(target));
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const trigger = wrapper.current?.querySelector('button');
    const link = disclosure.current?.querySelector('a');
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      trigger?.focus();
      setPosition(null);
    } else if (event.key === 'Tab' && !event.shiftKey && event.target === trigger && link) {
      event.preventDefault();
      link.focus();
    } else if (event.key === 'Tab' && event.target === link) {
      event.preventDefault();
      if (event.shiftKey) {
        trigger?.focus();
      } else {
        // The portal is at the document end; resume after its original trigger.
        const controls = Array.from(document.querySelectorAll<HTMLElement>('a[href],button,input,select,textarea,[tabindex]'))
          .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled')
            && !element.closest('[hidden],[inert]') && !disclosure.current?.contains(element)
            && (element as HTMLElement & { checkVisibility?: () => boolean }).checkVisibility?.() !== false);
        const next = controls[controls.indexOf(trigger!) + 1] || controls[0];
        setPosition(null);
        next?.focus();
      }
    }
  };
  useEffect(() => {
    if (!position) return;
    const close = () => setPosition(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [position]);
  if (!locked) return children;
  return <span ref={wrapper} className={`feature-action is-locked${position ? ' is-expanded' : ''}`}
    onMouseEnter={open}
    onMouseLeave={(event) => {
      if (!contains(event.relatedTarget) && !contains(document.activeElement)) setPosition(null);
    }} onFocus={open}
    onBlur={(event) => {
      if (!contains(event.relatedTarget)) setPosition(null);
    }}
    onKeyDown={handleKeyDown}>
    {React.cloneElement(children, {
      disabled: false,
      'aria-disabled': true,
      'aria-expanded': Boolean(position),
      'aria-controls': position ? id : undefined,
      'aria-describedby': position ? id : undefined,
      title: locked.reason,
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        open();
      },
    })}
    {position && isActive && createPortal(<span ref={disclosure} className="feature-action-disclosure" id={id} style={position}>
      <Lock size={13} aria-hidden="true" />
      <span>Unlock all Starter features. <a href={locked.upgradePath} onClick={(event) => event.stopPropagation()}>{getUpgradeLabel(account)}</a></span>
    </span>, portalContainer || document.body)}
  </span>;
};
export default FeatureAction;
